import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Directions,
    FlingGesture,
    Gesture,
} from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';

import { selectScreenReaderEnabled } from 'store/config/selectors';
import { setFocusToNow } from 'store/epg/actions';
import { StreamTypes } from 'store/player/actions.types';
import { selectChannels } from 'store/programs/selectors';
import { RBChannel } from 'store/railsBuilder/reducer.types';
import { selectIfKidsProfileActive } from 'store/user/selectors';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import { VideoItem } from 'services/data-manager/types';

import useGetLiveProgram from 'utils/hooks/useGetLiveProgram';
import { isWeb } from 'utils/responsiveness';

import { SwitchPlayerSource } from 'screens/PlayerModal/types';

type Params = {
    visible: boolean;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    embedded: boolean | undefined;
    fullscreen: boolean;
    subtitlesDialogVisible?: boolean;
    recordingActionsDialogVisible?: boolean;
    onSwitchTimeshiftModeOff(): void;
    onResetTimeshiftNotification(): void;
    onResetTimeshiftNotificationShown(): void;
    onSwitchPlayerSource: SwitchPlayerSource;
    setPlayerPaused: Dispatch<SetStateAction<boolean>>;
    setPlayerPausedByUser: Dispatch<SetStateAction<boolean>>;
    onUpdateBookmark(): void;
    onSetReadyToPlay(readyToPlay: boolean): void;
};

const SUPPORTED_STREAM_TYPES = [
    StreamTypes.live,
    StreamTypes.startover,
    StreamTypes.catchup,
];
export const DEFAULT_ZAPPING_CONFIG: ReturnType<typeof useChannelsZapping> = {
    zappingEnabled: false,
    flingGestures: undefined,
    channelsZapping: false,
    zappedChannelId: undefined,
    handleStopZapping: () => {},
};

function useChannelsZapping({
    visible,
    videoItem,
    playbackType,
    embedded,
    fullscreen,
    subtitlesDialogVisible,
    recordingActionsDialogVisible,
    onSwitchTimeshiftModeOff,
    onResetTimeshiftNotification,
    onResetTimeshiftNotificationShown,
    onSwitchPlayerSource,
    setPlayerPaused,
    setPlayerPausedByUser,
    onUpdateBookmark,
    onSetReadyToPlay,
}: Params): {
    zappingEnabled: boolean;
    flingGestures: FlingGesture[] | undefined;
    channelsZapping: boolean;
    zappedChannelId: string | undefined;
    handleStopZapping(): void;
} {
    const dispatch = useDispatch();

    const kidsProfileActive = useSelector(selectIfKidsProfileActive);
    const channels = useSelector(selectChannels);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const zappingEnabled = useMemo(
        () =>
            !isWeb &&
            !screenReaderEnabled &&
            visible &&
            !kidsProfileActive &&
            !!playbackType &&
            SUPPORTED_STREAM_TYPES.includes(playbackType) &&
            !(embedded && !fullscreen) &&
            !subtitlesDialogVisible &&
            !recordingActionsDialogVisible,
        [
            visible,
            embedded,
            fullscreen,
            playbackType,
            kidsProfileActive,
            screenReaderEnabled,
            subtitlesDialogVisible,
            recordingActionsDialogVisible,
        ],
    );

    const onGetLiveProgram = useGetLiveProgram();

    const [channelsZapping, setChannelsZapping] = useState(false);
    const [currentChannelIndex, setCurrentChannelIndex] = useState(
        channels.findIndex(
            ({ editorial: { id } }) =>
                videoItem?.channelId && id === videoItem?.channelId,
        ),
    );

    const timeoutRef = useRef<NodeJS.Timeout>(undefined);
    const prevChannelIndex = useRef(currentChannelIndex);

    const handleZapFinished = useCallback(
        async (channel: RBChannel) => {
            if (videoItem?.channelId !== channel.editorial.id) {
                const liveProgram = await onGetLiveProgram(
                    channel.editorial.id,
                );

                if (playbackType === StreamTypes.catchup) {
                    onUpdateBookmark();
                }

                onSwitchTimeshiftModeOff();
                onResetTimeshiftNotification();
                onResetTimeshiftNotificationShown();
                setPlayerPaused(false);
                setPlayerPausedByUser(false);
                await onSwitchPlayerSource(
                    parseVideoItem({ channel, program: liveProgram }),
                    StreamTypes.live,
                );
                onSetReadyToPlay(false);

                dispatch(setFocusToNow(true));
            }

            setChannelsZapping(false);
        },
        [
            videoItem?.channelId,
            playbackType,
            onGetLiveProgram,
            onResetTimeshiftNotification,
            onResetTimeshiftNotificationShown,
            onSetReadyToPlay,
            onSwitchPlayerSource,
            onSwitchTimeshiftModeOff,
            setPlayerPaused,
            setPlayerPausedByUser,
            onUpdateBookmark,
        ],
    );

    const flingGestures = useMemo(
        () => [
            Gesture.Fling()
                .direction(Directions.LEFT)
                .runOnJS(true)
                .numberOfPointers(1)
                .enabled(zappingEnabled)
                .hitSlop({
                    horizontal: -50,
                    bottom: -75,
                })
                .onEnd(() => {
                    setCurrentChannelIndex((prevCurrentChannelIndex) => {
                        if (prevCurrentChannelIndex >= 0 && visible) {
                            if (
                                prevCurrentChannelIndex ===
                                channels.length - 1
                            ) {
                                return 0;
                            }

                            return prevCurrentChannelIndex + 1;
                        }

                        return prevCurrentChannelIndex;
                    });
                }),
            Gesture.Fling()
                .direction(Directions.RIGHT)
                .runOnJS(true)
                .numberOfPointers(1)
                .enabled(zappingEnabled)
                .hitSlop({
                    horizontal: -50,
                    bottom: -75,
                })
                .onEnd(() => {
                    setCurrentChannelIndex((prevCurrentChannelIndex) => {
                        if (prevCurrentChannelIndex >= 0 && visible) {
                            if (prevCurrentChannelIndex === 0) {
                                return channels.length - 1;
                            }

                            return prevCurrentChannelIndex - 1;
                        }

                        return prevCurrentChannelIndex;
                    });
                }),
        ],
        [channels.length, zappingEnabled, visible],
    );

    useEffect(() => {
        if (!zappingEnabled) {
            setCurrentChannelIndex(-1);
            setChannelsZapping(false);
        }
    }, [zappingEnabled]);

    useEffect(() => {
        if (zappingEnabled) {
            setCurrentChannelIndex(
                channels.findIndex(
                    ({ editorial: { id } }) => id === videoItem?.channelId,
                ),
            );
        }
    }, [zappingEnabled, videoItem?.channelId]);

    useEffect(() => {
        if (zappingEnabled) {
            if (
                prevChannelIndex.current !== currentChannelIndex &&
                prevChannelIndex.current >= 0 &&
                currentChannelIndex >= 0
            ) {
                setChannelsZapping(true);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined;
                }
                timeoutRef.current = setTimeout(() => {
                    handleZapFinished(channels[currentChannelIndex]);
                }, 1000);
            }

            prevChannelIndex.current = currentChannelIndex;
        }
    }, [zappingEnabled, currentChannelIndex]);

    const handleStopZapping = useCallback(() => {
        setChannelsZapping(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    }, []);

    return useMemo(
        () => ({
            zappingEnabled,
            flingGestures,
            channelsZapping,
            zappedChannelId: channels[currentChannelIndex]?.editorial?.id,
            handleStopZapping,
        }),
        [
            channels,
            channelsZapping,
            currentChannelIndex,
            flingGestures,
            zappingEnabled,
        ],
    );
}

export default useChannelsZapping;
